import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ROUTE_PATHS } from '@/lib/index';
import { useLanguage, Language } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { IMAGES } from '@/assets/images';
const LANG_OPTIONS: {
  value: Language;
  label: string;
}[] = [{
  value: 'en',
  label: 'Eng'
}, {
  value: 'zh',
  label: '中文'
}];

// ── Version check — runs once per session when user lands on the login page ──
const VERSION_CHECK_KEY = 'loginVersionChecked';
async function runVersionCheck(): Promise<void> {
  // Only run once per browser session to avoid reload loops
  if (sessionStorage.getItem(VERSION_CHECK_KEY)) return;
  // Mark immediately so any reload doesn't re-run this
  sessionStorage.setItem(VERSION_CHECK_KEY, '1');
  try {
    // Fetch index.html with a cache-buster so we always get the latest from server
    const res = await fetch(`/?_cb=${__BUILD_TIME__}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    if (!res.ok) return;
    const html = await res.text();

    // Extract the <script type="module" src="..."> from the fetched HTML
    const remoteMatch = html.match(/<script[^>]+type=["']module["'][^>]+src=["']([^"']+)["']/i) || html.match(/<script[^>]+src=["']([^"']+)["'][^>]+type=["']module["']/i);
    if (!remoteMatch) return;
    const remoteSrc = remoteMatch[1];

    // Extract the <script type="module" src="..."> loaded in the current page
    const localScript = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
    if (!localScript) return;
    const localSrc = localScript.getAttribute('src') || '';

    // Compare just the filename (the hash changes each build)
    const remoteFile = remoteSrc.split('/').pop()?.split('?')[0] ?? '';
    const localFile = localSrc.split('/').pop()?.split('?')[0] ?? '';
    if (remoteFile && localFile && remoteFile !== localFile) {
      console.log(`[VersionCheck] New build detected (${localFile} → ${remoteFile}). Reloading…`);
      window.location.reload();
    }
  } catch (_) {
    // Network error or parse error — silently skip, don't break login
  }
}

// ── Main Login page ──────────────────────────────────────────────────────────
const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();

  // Run version check exactly once when the login page mounts
  useEffect(() => {
    runVersionCheck();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLocked(false);
    setIsLoading(true);
    const WRONG_CREDS_MSG = 'Wrong Username and Password, Please try again.';
    const BANNED_MSG = 'Account banned. Please contact your upline.';
    const sanitiseError = (raw: string) => {
      // Always pass through banned / locked / remaining-attempts messages as-is
      if (raw.toLowerCase().includes('banned')) return BANNED_MSG;
      if (raw.toLowerCase().includes('locked')) return raw;
      if (raw.toLowerCase().includes('remaining')) return raw;
      // Replace any technical / edge-function errors with a friendly message
      if (
        raw.toLowerCase().includes('non-2xx') ||
        raw.toLowerCase().includes('edge function') ||
        raw.toLowerCase().includes('invalid') ||
        raw.toLowerCase().includes('incorrect') ||
        raw.toLowerCase().includes('failed') ||
        raw.toLowerCase().includes('unauthorized') ||
        raw.toLowerCase().includes('wrong')
      ) return WRONG_CREDS_MSG;
      return raw;
    };
    try {
      const result = await login(username, password);
      if (!result || !result.success) {
        const raw = result?.error || WRONG_CREDS_MSG;
        if (raw.toLowerCase().includes('locked') || raw.toLowerCase().includes('banned')) setIsLocked(true);
        setError(sanitiseError(raw));
        return;
      }
      navigate(ROUTE_PATHS.MATCHES);
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : WRONG_CREDS_MSG;
      setError(sanitiseError(raw));
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="relative min-h-screen w-full flex items-center justify-center bg-background overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img src={IMAGES.STADIUM_2} alt="Stadium Background" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70 bg-[rgb(255,255,255)]" />
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="relative z-10 w-full max-w-md px-4">
        <div className="flex flex-col items-center mb-8" />

        <Card className="border-border shadow-2xl bg-card/80 backdrop-blur-sm">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">{t.login.welcomeBack}</CardTitle>
            <CardDescription className="text-sm text-muted-foreground xl:text-xs">
              {t.login.enterCredentials}
            </CardDescription>
            {/* Language switcher — below description */}
            <div className="flex items-center gap-1 pt-1">
              {LANG_OPTIONS.map(opt => <button key={opt.value} onClick={() => setLanguage(opt.value)} className={['px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200', language === opt.value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-accent'].join(' ')}>
                  {opt.label}
                </button>)}
            </div>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-1">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{isLocked ? t.login.accountLocked : t.login.error}</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>}

              <div className="space-y-2">
                <Label htmlFor="username">{t.login.username}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input id="username" placeholder={t.login.usernamePlaceholder} className="pl-10" value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" required />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t.login.password}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="" className="pl-10 pr-10" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-4">
              <Button type="submit" className="w-full h-11 font-semibold text-base shadow-lg" disabled={isLoading}>
                {isLoading ? t.login.authenticating : t.login.signIn}
              </Button>

              <div className="w-full border-t border-border pt-4">
                <p className="text-[10px] text-muted-foreground text-center italic mt-1">
                  {t.login.copyright}
                </p>
              </div>
            </CardFooter>
          </form>
        </Card>

        <div className="mt-8 flex justify-center gap-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <ShieldCheck className="h-4 w-4" />
            <span className="text-xs">{t.login.secureSSL}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Lock className="h-4 w-4" />
            
          </div>
        </div>
      </motion.div>
    </div>;
};
export default Login;